import posthog from 'posthog-js';
import { validateEventProperties } from './analytics.types';

// PostHog configuration
const POSTHOG_KEY = process.env.REACT_APP_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com';
const DEBUG_MODE = process.env.NODE_ENV === 'development';

class AnalyticsService {
  constructor() {
    this.initialized = false;
    this.userId = null;
    this.debugMode = DEBUG_MODE;
  }

  // Initialize PostHog
  init() {
    if (!POSTHOG_KEY) {
      console.warn('PostHog API key not found. Analytics disabled.');
      return;
    }

    if (this.initialized) {
      return;
    }

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      persistence: 'localStorage',
      capture_pageview: false, // We'll manually track page views with route changes
      capture_pageleave: true, // Track when users leave pages
      session_recording: {
        maskAllInputs: true, // Mask sensitive input data in recordings
        maskTextContent: false,
        // Disable recording of certain interactions that cause issues
        recordCanvas: false,
        recordCrossOriginIframes: false,
        // Add error handling for DOM access
        maskTextFn: (text) => {
          try {
            return text;
          } catch (e) {
            return '';
          }
        }
      },
      // Configure autocapture to avoid permission errors
      autocapture: {
        dom_event_allowlist: ['click', 'change', 'submit'], // Exclude contextmenu (right-click)
        css_selector_allowlist: ['a', 'button', 'form', 'input', 'select', 'textarea', '[data-analytics]'],
        element_allowlist: ['a', 'button', 'form', 'input', 'select', 'textarea'],
        // Disable capture on elements that might cause issues
        capture_copied_text: false
      },
      loaded: (posthog) => {
        console.log('PostHog initialized successfully');
        if (this.debugMode) {
          console.log('🔍 PostHog Debug Mode Enabled');
          console.log('📊 PostHog Instance:', posthog);
          console.log('🔑 API Host:', POSTHOG_HOST);
        }
      }
    });

    this.initialized = true;
    
    // Add global error handler to suppress PostHog recorder errors
    window.addEventListener('error', (event) => {
      if (event.message && event.message.includes('Permission denied to access property') && 
          event.filename && event.filename.includes('posthog.com')) {
        // Suppress PostHog recorder errors
        event.preventDefault();
        if (this.debugMode) {
          console.warn('Suppressed PostHog recorder error:', event.message);
        }
      }
    });
    
    // Expose to window for debugging in development
    if (this.debugMode) {
      window.__analytics = this;
    }
  }

  // Identify user after login/register
  identify(user) {
    if (!this.initialized || !user) return;

    this.userId = user.id;
    
    const userProperties = {
      email: user.email,
      username: user.username,
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      is_alpha_tester: user.is_alpha_tester || false,
      email_verified: user.email_verified,
      trial_exams_used: user.trial_exams_used || 0,
      subscription_status: user.subscription_status,
      created_at: user.created_at,
    };
    
    if (this.debugMode) {
      console.log(`👤 [Analytics] Identifying user: ${user.id}`, userProperties);
    }
    
    // Identify the user in PostHog
    posthog.identify(user.id, userProperties);

    // Set user properties that might change
    this.setUserProperties({
      last_login: new Date().toISOString(),
      login_count: (user.login_count || 0) + 1,
    });
  }

  // Clear user identification on logout
  reset() {
    if (!this.initialized) return;
    
    posthog.reset();
    this.userId = null;
  }

  // Track custom events
  track(event, properties = {}) {
    if (!this.initialized) return;

    // Validate event properties in debug mode
    if (this.debugMode) {
      validateEventProperties(event, properties);
    }

    // Add timestamp and session info
    const enrichedProperties = {
      ...properties,
      timestamp: new Date().toISOString(),
      session_id: posthog.get_session_id ? posthog.get_session_id() : null,
    };

    if (this.debugMode) {
      console.log(`📊 [Analytics] Event: ${event}`, enrichedProperties);
    }

    posthog.capture(event, enrichedProperties);
  }

  // Track page views
  trackPageView(path, properties = {}) {
    if (!this.initialized) return;

    const pageViewData = {
      $current_url: window.location.href,
      path: path,
      ...properties,
    };
    
    if (this.debugMode) {
      console.log(`📄 [Analytics] Page View: ${path}`, pageViewData);
    }

    posthog.capture('$pageview', pageViewData);
  }

  // Set user properties
  setUserProperties(properties) {
    if (!this.initialized) return;

    posthog.people.set(properties);
  }

  // Track session duration
  startSession() {
    if (!this.initialized) return;

    const sessionStart = new Date().toISOString();
    sessionStorage.setItem('session_start', sessionStart);
    
    this.track('session_started', {
      session_start: sessionStart,
    });
  }

  endSession() {
    if (!this.initialized) return;

    const sessionStart = sessionStorage.getItem('session_start');
    if (sessionStart) {
      const duration = new Date() - new Date(sessionStart);
      const durationMinutes = Math.round(duration / 60000);
      
      this.track('session_ended', {
        session_duration_ms: duration,
        session_duration_minutes: durationMinutes,
      });
      
      sessionStorage.removeItem('session_start');
    }
  }

  // Track time spent on page
  startPageTimer(pageName) {
    if (!this.initialized) return;

    const pageStart = new Date().getTime();
    sessionStorage.setItem(`page_start_${pageName}`, pageStart);
  }

  endPageTimer(pageName) {
    if (!this.initialized) return;

    const startTime = sessionStorage.getItem(`page_start_${pageName}`);
    if (startTime) {
      const duration = new Date().getTime() - parseInt(startTime);
      const durationSeconds = Math.round(duration / 1000);
      
      this.track('page_time_spent', {
        page: pageName,
        duration_ms: duration,
        duration_seconds: durationSeconds,
      });
      
      sessionStorage.removeItem(`page_start_${pageName}`);
    }
  }

  // Track feature usage
  trackFeature(featureName, properties = {}) {
    this.track('feature_used', {
      feature: featureName,
      ...properties,
    });
  }

  // Track exam generation
  trackExamGeneration(examType, properties = {}) {
    this.track('exam_generated', {
      exam_type: examType,
      ...properties,
    });
  }

  // Track errors
  trackError(error, context = {}) {
    if (!this.initialized) return;

    this.track('error_occurred', {
      error_message: error.message || error,
      error_stack: error.stack,
      ...context,
    });
  }

  // Subscription tracking methods
  trackSubscriptionPlanViewed(planId, planName, properties = {}) {
    this.track('subscription_plan_viewed', {
      plan_id: planId,
      plan_name: planName,
      ...properties,
    });
  }

  trackSubscriptionCheckoutStarted(planId, planName, priceCents, properties = {}) {
    this.track('subscription_checkout_started', {
      plan_id: planId,
      plan_name: planName,
      price_cents: priceCents,
      price_dollars: priceCents / 100,
      ...properties,
    });
  }

  trackSubscriptionCheckoutCompleted(planId, planName, priceCents, properties = {}) {
    this.track('subscription_checkout_completed', {
      plan_id: planId,
      plan_name: planName,
      price_cents: priceCents,
      price_dollars: priceCents / 100,
      ...properties,
    });
  }

  trackSubscriptionCheckoutAbandoned(planId, planName, properties = {}) {
    this.track('subscription_checkout_abandoned', {
      plan_id: planId,
      plan_name: planName,
      ...properties,
    });
  }

  // Track checkout initiated (alias for subscription checkout started)
  trackCheckoutInitiated(context, properties = {}) {
    this.track('checkout_initiated', {
      context: context,
      ...properties,
    });
  }

  trackSubscriptionCancelled(reason, properties = {}) {
    this.track('subscription_cancelled', {
      cancellation_reason: reason,
      ...properties,
    });
  }

  trackSubscriptionReactivated(planId, planName, properties = {}) {
    this.track('subscription_reactivated', {
      plan_id: planId,
      plan_name: planName,
      ...properties,
    });
  }

  trackSubscriptionPlanChanged(fromPlanId, toPlanId, changeType, properties = {}) {
    this.track('subscription_plan_changed', {
      from_plan_id: fromPlanId,
      to_plan_id: toPlanId,
      change_type: changeType, // 'upgrade' or 'downgrade'
      ...properties,
    });
  }

  // Usage limit tracking
  trackExamLimitReached(examCount, examLimit, properties = {}) {
    this.track('exam_limit_reached', {
      exam_count: examCount,
      exam_limit: examLimit,
      ...properties,
    });
  }

  trackSubscriptionPromptShown(promptType, context, properties = {}) {
    this.track('subscription_prompt_shown', {
      prompt_type: promptType,
      context: context,
      ...properties,
    });
  }

  trackFeatureGateEncountered(featureName, requiredPlan, properties = {}) {
    this.track('feature_gate_encountered', {
      feature_name: featureName,
      required_plan: requiredPlan,
      ...properties,
    });
  }

  // Alpha Tester tracking methods
  trackAlphaTesterRegistered(referralSource, referrerId, properties = {}) {
    this.track('alpha_tester_registered', {
      referral_source: referralSource,
      referrer_id: referrerId,
      ...properties,
    });
  }

  trackAlphaTesterOnboarded(daysSinceRegistration, firstLogin, examsGenerated, properties = {}) {
    this.track('alpha_tester_onboarded', {
      days_since_registration: daysSinceRegistration,
      first_login: firstLogin,
      exams_generated: examsGenerated,
      ...properties,
    });
  }

  trackAlphaReferralLinkShared(shareMethod, referralsUsed, referralsRemaining, maxReferrals, properties = {}) {
    this.track('alpha_referral_link_shared', {
      share_method: shareMethod,
      referrals_used: referralsUsed,
      referrals_remaining: referralsRemaining,
      max_referrals: maxReferrals,
      ...properties,
    });
  }

  trackAlphaReferralVisited(referralToken, referrerId, isValid, errorReason = null, properties = {}) {
    this.track('alpha_referral_visited', {
      referral_token: referralToken,
      referrer_id: referrerId,
      is_valid: isValid,
      error_reason: errorReason,
      ...properties,
    });
  }

  trackAlphaReferralConverted(referralToken, referrerId, daysSinceVisit, registrationCompleted, properties = {}) {
    this.track('alpha_referral_converted', {
      referral_token: referralToken,
      referrer_id: referrerId,
      days_since_visit: daysSinceVisit,
      registration_completed: registrationCompleted,
      ...properties,
    });
  }

  trackAlphaDiscountApplied(couponCode, discountPercentage, planId, planName, originalPrice, discountedPrice, properties = {}) {
    this.track('alpha_discount_applied', {
      coupon_code: couponCode,
      discount_percentage: discountPercentage,
      plan_id: planId,
      plan_name: planName,
      original_price_cents: originalPrice,
      discounted_price_cents: discountedPrice,
      ...properties,
    });
  }

  // Payment tracking methods
  trackPaymentMethodAdded(methodType, methodBrand, isDefault, properties = {}) {
    this.track('payment_method_added', {
      payment_method_type: methodType,
      payment_method_brand: methodBrand,
      is_default: isDefault,
      ...properties,
    });
  }

  trackPaymentFailed(errorType, errorMessage, amountCents, currency, subscriptionId = null, properties = {}) {
    this.track('payment_failed', {
      error_type: errorType,
      error_message: errorMessage,
      amount_cents: amountCents,
      currency: currency,
      subscription_id: subscriptionId,
      ...properties,
    });
  }

  trackCouponApplied(couponCode, discountPercentage, discountAmountCents, planId, planName, properties = {}) {
    this.track('coupon_applied', {
      coupon_code: couponCode,
      discount_percentage: discountPercentage,
      discount_amount_cents: discountAmountCents,
      plan_id: planId,
      plan_name: planName,
      ...properties,
    });
  }

  trackTrialStarted(trialDays, planId, planName, isAlphaTester, properties = {}) {
    this.track('trial_started', {
      trial_days: trialDays,
      plan_id: planId,
      plan_name: planName,
      is_alpha_tester: isAlphaTester,
      ...properties,
    });
  }

  trackTrialEnded(trialDaysUsed, convertedToPaid, planId = null, planName = null, properties = {}) {
    this.track('trial_ended', {
      trial_days_used: trialDaysUsed,
      converted_to_paid: convertedToPaid,
      plan_id: planId,
      plan_name: planName,
      ...properties,
    });
  }

  trackTrialConverted(daysInTrial, planId, planName, priceCents, couponApplied, couponCode = null, properties = {}) {
    this.track('trial_converted', {
      days_in_trial: daysInTrial,
      plan_id: planId,
      plan_name: planName,
      price_cents: priceCents,
      coupon_applied: couponApplied,
      coupon_code: couponCode,
      ...properties,
    });
  }

  // Check if analytics is enabled
  isEnabled() {
    return this.initialized && !!POSTHOG_KEY;
  }
  
  // Debug helper methods
  getDebugInfo() {
    if (!this.initialized) return { initialized: false };
    
    return {
      initialized: this.initialized,
      userId: this.userId,
      distinctId: posthog.get_distinct_id ? posthog.get_distinct_id() : null,
      sessionId: posthog.get_session_id ? posthog.get_session_id() : null,
      apiHost: POSTHOG_HOST,
      hasApiKey: !!POSTHOG_KEY,
      debugMode: this.debugMode,
      featureFlags: posthog.getFeatureFlags ? posthog.getFeatureFlags() : {},
      personProperties: posthog.get_property ? posthog.get_property() : {},
    };
  }
  
  // Force flush events (useful for testing)
  flush() {
    if (this.initialized) {
      posthog.flush();
      if (this.debugMode) {
        console.log('🚀 [Analytics] Events flushed to PostHog');
      }
    }
  }
}

// Create singleton instance
const analytics = new AnalyticsService();

export default analytics;